import { useEffect, type ReactNode } from "react";
import { useRouterState } from "@tanstack/react-router";
import { BottomTabBar } from "@/components/BottomTabBar";
import { SiteHeader } from "@/components/landing/SiteHeader";
import { NextStepDialogs } from "@/components/NextStep";
import { useAppVisit } from "@/hooks/use-app-visit";
import { isBodyScrollLocked } from "@/hooks/use-body-scroll-lock";
import { ExistingUserOnboardingNudge } from "@/components/ExistingUserOnboardingNudge";

/**
 * Страховка: vaul (Drawer) и radix могут оставить inline-стили на <body>
 * (transform, position: fixed, left/right/top, overflow, pointer-events).
 * Любой transform на предке ломает position: fixed у нижнего меню, а left/width —
 * дают горизонтальный сдвиг. Чистим при навигации и когда ни одно окно не открыто.
 */
function cleanupBodyStyles() {
  if (typeof document === "undefined") return;
  if (isBodyScrollLocked()) return;
  if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [data-vaul-drawer][data-state="open"]')) return;
  const s = document.body.style;
  for (const prop of ["transform", "position", "top", "left", "right", "width", "overflow", "pointerEvents", "transition", "transformOrigin"] as const) {
    if (s[prop]) s[prop] = "";
  }
}

function useResetBodyTransform() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    cleanupBodyStyles();
  }, [pathname]);
  useEffect(() => {
    if (typeof MutationObserver === "undefined") return;
    let timer: number | undefined;
    const observer = new MutationObserver(() => {
      window.clearTimeout(timer);
      // Даём анимации закрытия завершиться, потом проверяем.
      timer = window.setTimeout(cleanupBodyStyles, 600);
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ["style"] });
    return () => {
      observer.disconnect();
      window.clearTimeout(timer);
    };
  }, []);
}

/** Оболочка вошедшего пользователя: шапка, подсказки, нижняя панель. */
export function AppShell({ children }: { children: ReactNode }) {
  useResetBodyTransform();
  useAppVisit();
  return (
    <div className="min-w-0 overflow-x-clip">
      <SiteHeader />
      <NextStepDialogs />
      <ExistingUserOnboardingNudge />
      <div className="min-w-0 pb-20 md:pb-0">{children}</div>
      <BottomTabBar />
    </div>
  );
}
